/**
 * Twin-test `visibleIf` names, shared by the writer-side check (Nest) and the
 * gate's edits (browser), so both rename a condition the same way.
 *
 * A condition names earlier questions by position (`q1` is the first, `q2`
 * the second). When a question leaves the list, every later name shifts, so a
 * condition is renamed by question identity: each name SurveyJS lists for it
 * (`ConditionRunner.getVariables()`) is pointed at the same question's new
 * position. Nothing here parses an expression or reads an answer value of any
 * particular survey.
 *
 * Cache scope: one-shot, not cached (pure string surgery, no LLM).
 */

import { ConditionRunner } from 'survey-core';

/** The schema bound on a twin-test `visibleIf`, in characters (after trimming). */
export const TWIN_TEST_VISIBLE_IF_MAX_CHARS = 200;

/** The stable SurveyJS name of the question at `index` (0-based): `q1`, `q2`, ... */
export function twinTestQuestionName(index: number): string {
  return `q${index + 1}`;
}

/** The question name a SurveyJS variable reads: `q1.r1` and `q1[0]` both read `q1`. */
export function twinTestVariableQuestionName(variable: string): string {
  const cut = variable.search(/[.[]/);
  return cut === -1 ? variable : variable.slice(0, cut);
}

/** The 0-based position a stable name reads (`q3` is 2), or null for any other name. */
export function twinTestQuestionNameIndex(name: string): number | null {
  const match = /^q(\d+)$/.exec(name);
  return match ? Number(match[1]) - 1 : null;
}

/**
 * The 0-based positions a condition reads, deduplicated, in first-read order.
 * Null when SurveyJS cannot read the condition at all.
 */
export function twinTestConditionQuestionIndexes(
  visibleIf: string,
): Array<number> | null {
  const runner = new ConditionRunner(visibleIf);
  if (!runner.canRun()) {
    return null;
  }
  const indexes = runner
    .getVariables()
    .map((variable) =>
      twinTestQuestionNameIndex(twinTestVariableQuestionName(variable)),
    )
    .filter((index): index is number => index !== null);
  return [...new Set(indexes)];
}

/**
 * The earlier questions a conditional follow-up at `index` reads, or null when
 * the condition does not make it one: it is blank or past the schema bound,
 * SurveyJS cannot read it, it reads no question, or it reads anything but an
 * earlier question by its exact stable name (`q01` and `q0` are not names).
 * A follow-up takes no per-test question slot (live QA f2). This is the one
 * rule: Nest's writer-side ceiling (`readTwinTestFollowUpParents`) and the
 * gate's cap both call it. Structure only: which names SurveyJS lists, never
 * what is compared.
 */
export function twinTestFollowUpParentIndexes(
  visibleIf: string,
  index: number,
): Array<number> | null {
  const condition = visibleIf.trim();
  if (condition.length === 0 || condition.length > TWIN_TEST_VISIBLE_IF_MAX_CHARS) {
    return null;
  }
  const runner = new ConditionRunner(condition);
  if (!runner.canRun()) {
    return null;
  }
  const indexes = runner.getVariables().map((variable) => {
    const name = twinTestVariableQuestionName(variable);
    const position = twinTestQuestionNameIndex(name);
    return position !== null &&
      position < index &&
      twinTestQuestionName(position) === name
      ? position
      : null;
  });
  if (indexes.length === 0 || indexes.some((position) => position === null)) {
    return null;
  }
  return [...new Set(indexes as Array<number>)];
}

/**
 * What a condition reads off the question at `questionIndex`, one entry per
 * SurveyJS variable that names it: the member path after the question name
 * (`q1.rb` gives `.rb`, `q1[0]` gives `[0]`, `q1` gives `''`). Empty when the
 * condition does not read that question; null when SurveyJS cannot read it.
 */
export function twinTestConditionQuestionPaths(
  visibleIf: string,
  questionIndex: number,
): Array<string> | null {
  const runner = new ConditionRunner(visibleIf);
  if (!runner.canRun()) {
    return null;
  }
  const paths: Array<string> = [];
  for (const variable of runner.getVariables()) {
    const name = twinTestVariableQuestionName(variable);
    if (twinTestQuestionNameIndex(name) === questionIndex) {
      paths.push(variable.slice(name.length));
    }
  }
  return paths;
}

/**
 * Whether a condition reads the row `rowId` of the grid question at
 * `questionIndex`. A path that names a row (`{q1.rb}`, `{q1.rb.a}`) reads that
 * row only; any other read of the question (the whole grid, an index) cannot
 * be pinned to one row, so it counts as reading every row (a hit only ever
 * blocks an edit at the gate). A condition SurveyJS cannot read names no
 * question, so it reads no row: the same answer the question-level dependents
 * give, and the structural check reports that condition on submit.
 */
export function twinTestConditionReadsRow(
  visibleIf: string,
  questionIndex: number,
  rowId: string,
): boolean {
  const paths = twinTestConditionQuestionPaths(visibleIf, questionIndex);
  if (!paths) {
    return false;
  }
  return paths.some((path) => {
    const named = /^\.([^.[]+)/.exec(path);
    return named ? named[1] === rowId : true;
  });
}

/**
 * Re-point one condition at a list some questions left.
 *
 * `survivorIndexByOldIndex[i]` is the new position of the question that was at
 * `i`, or null when it left. Names of questions that left come back in
 * `droppedIndexes` (old positions) and the condition is returned as written,
 * for the caller to refuse or report. A condition SurveyJS cannot read is
 * returned unchanged for the structural check to report.
 */
export function remapTwinTestConditionNames(
  visibleIf: string,
  survivorIndexByOldIndex: ReadonlyArray<number | null>,
): { visibleIf: string; droppedIndexes: Array<number> } {
  const read = twinTestConditionQuestionIndexes(visibleIf);
  if (read === null) {
    return { visibleIf, droppedIndexes: [] };
  }
  const droppedIndexes = read.filter(
    (index) =>
      index < survivorIndexByOldIndex.length &&
      survivorIndexByOldIndex[index] === null,
  );
  if (droppedIndexes.length > 0) {
    return { visibleIf, droppedIndexes };
  }
  const renamed = visibleIf.replace(
    /\{(q\d+)(?=[}.[])/g,
    (whole: string, name: string) => {
      const index = twinTestQuestionNameIndex(name);
      const survivor =
        index !== null && index < survivorIndexByOldIndex.length
          ? survivorIndexByOldIndex[index]
          : null;
      return survivor === null || survivor === undefined
        ? whole
        : `{${twinTestQuestionName(survivor)}`;
    },
  );
  return { visibleIf: renamed, droppedIndexes: [] };
}

/**
 * Whether a condition compares the question at `questionIndex` against the
 * choice text `choice`: it reads that question, and the choice appears in it
 * as a quoted literal. Conservative on purpose: a hit only ever blocks an edit
 * at the gate, it never changes what runs.
 */
export function twinTestConditionReadsChoice(
  visibleIf: string,
  questionIndex: number,
  choice: string,
): boolean {
  const read = twinTestConditionQuestionIndexes(visibleIf);
  if (!read || !read.includes(questionIndex) || choice.length === 0) {
    return false;
  }
  return visibleIf.includes(`'${choice}'`) || visibleIf.includes(`"${choice}"`);
}

/** A condition as a reviewer reads it: `{q1} = 'Yes'` becomes `Q1 = 'Yes'`. */
export function twinTestConditionDisplay(visibleIf: string): string {
  return visibleIf.replace(
    /\{q(\d+)((?:[.[][^}]*)?)\}/g,
    (_whole: string, position: string, path: string) => `Q${position}${path}`,
  );
}
